/**
 * @fileoverview Hook for the "Data Repair — Inspection delivery duplicates"
 *               admin page.
 * @module system-web/hooks/useDeliveryDuplicateRepair
 *
 * Responsibilities:
 * - Subscribe to `background_jobs` + `tenants` via liveQuery (pure read).
 * - Map jobs into per-tenant rows { tenant, latestScan, latestApply,
 *   latestRevert, activeJob }.
 * - Wrap scan/apply/revert service calls with submitting/error state.
 *
 * Dependencies:
 * - dexie (liveQuery)
 * - ../services/deliveryDuplicateRepairService
 *
 * Architecture:
 * - The liveQuery observed function is a pure Dexie read — no writes, no API
 *   calls (per the liveQuery contract).
 * - `activeJob` = the tenant's pending/running job (at most one per tenant —
 *   the server enforces this with 409).
 *
 * @localFirst
 * Source of Truth: `background_jobs` + `tenants` Dexie tables.
 * Write Flow: actions → API POST → server job → synced job rows refresh
 *   the liveQuery.
 * Sync Strategy: standard pull for `background_jobs` and `tenants`.
 * Conflict Resolution: server wins.
 *
 * @hook
 * Purpose: Drive DeliveryDuplicateRepairPage from local-first Dexie state.
 * Returns: rows, isLoading, isSubmitting, error, scanTenants, applyScan,
 *   revertApply.
 * Side Effects: liveQuery subscription only.
 * Offline Behavior: rows render from the last synced snapshot; actions
 *   require connectivity and surface errors.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { liveQuery } from 'dexie';
import {
  listDeliveryRepairJobs,
  listRepairTenants,
  startDeliveryDuplicateScan,
  applyDeliveryDuplicateRepair,
  revertDeliveryDuplicateRepair,
  type DeliveryRepairJob,
  type RepairTenantRow,
} from '../services/deliveryDuplicateRepairService';

const ACTIVE_STATUSES: ReadonlyArray<string> = ['pending', 'running'];

/**
 * @contract TenantRepairRow
 * Purpose: Per-department aggregate for the repair table.
 * Fields:  tenant, latestScan, latestApply, latestRevert, activeJob.
 * Rules:   "latest" = newest by startedAt.
 * Sync:    Derived from synced `background_jobs` rows.
 */
export interface TenantRepairRow {
  tenant: RepairTenantRow;
  latestScan?: DeliveryRepairJob;
  latestApply?: DeliveryRepairJob;
  latestRevert?: DeliveryRepairJob;
  activeJob?: DeliveryRepairJob;
}

async function readRepairState(): Promise<{ tenants: RepairTenantRow[]; jobs: DeliveryRepairJob[] }> {
  const [tenants, jobs] = await Promise.all([listRepairTenants(), listDeliveryRepairJobs()]);
  return { tenants, jobs };
}

export function useDeliveryDuplicateRepair() {
  const [tenants, setTenants] = useState<RepairTenantRow[]>([]);
  const [jobs, setJobs] = useState<DeliveryRepairJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // liveQuery — pure Dexie read only
  useEffect(() => {
    const subscription = liveQuery(readRepairState).subscribe({
      next: (state) => {
        setTenants(state.tenants);
        setJobs(state.jobs);
        setIsLoading(false);
      },
      error: (err) => {
        console.error('[useDeliveryDuplicateRepair] liveQuery error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load repair jobs');
        setIsLoading(false);
      },
    });
    return () => subscription.unsubscribe();
  }, []);

  const rows = useMemo<TenantRepairRow[]>(() => {
    const byTenant = new Map<string, DeliveryRepairJob[]>();
    for (const job of jobs) {
      const key = String(job.tenantId ?? '');
      const list = byTenant.get(key) ?? [];
      list.push(job);
      byTenant.set(key, list);
    }
    return tenants.map((tenant) => {
      const tenantJobs = byTenant.get(tenant._id) ?? [];
      const latestOf = (jobType: string) =>
        tenantJobs.find((j) => j.jobType === jobType && !ACTIVE_STATUSES.includes(j.status));
      return {
        tenant,
        latestScan: latestOf('deliveryDuplicateScan'),
        latestApply: latestOf('deliveryDuplicateApply'),
        latestRevert: latestOf('deliveryDuplicateRevert'),
        activeJob: tenantJobs.find((j) => ACTIVE_STATUSES.includes(j.status)),
      };
    });
  }, [tenants, jobs]);

  const scanTenants = useCallback(async (tenantIds: string[]): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await startDeliveryDuplicateScan(tenantIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start scan');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const applyScan = useCallback(async (scanJobId: string, confirmTenantName: string): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await applyDeliveryDuplicateRepair(scanJobId, confirmTenantName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start apply');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const revertApply = useCallback(async (applyJobId: string, confirmTenantName: string): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await revertDeliveryDuplicateRepair(applyJobId, confirmTenantName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start revert');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return { rows, isLoading, isSubmitting, error, scanTenants, applyScan, revertApply };
}
